import { Fragment, h } from "preact";

import BlockedState from "./BlockedState.jsx";
import Checklist from "./Checklist.jsx";
import JobSearchSummary from "./JobSearchSummary.jsx";
import PreAssessmentSummary from "./PreAssessmentSummary.jsx";
import LocationFinder from "./LocationFinder.jsx";
import Questionnaire from "./Questionnaire.jsx";
import QuestionnaireResultDisclaimer from "./QuestionnaireResultDisclaimer.jsx";
import Page from "./Page.jsx";
import ProductChooser from "./taskModules/ProductChooser.jsx";
import Registration from "./taskModules/Registration.jsx";
import ProductPage from "./ProductPage.jsx";

import SalaryCalculator from "./SalaryCalculator.jsx";
import { sendError } from "../errors";
import { useDispatch, useSelector } from "react-redux";
import {
  changeModule,
  fetchTasks,
  markTaskAsDone,
  restartTask,
  selectTaskLength,
  setHideFullTaskList,
} from "../store";
import { classNames } from "../helpers.js";
import TaskFooter from "./TaskFooter.jsx";

const MODULES = {
  checklist: ({ config, task, clickHandlers }) => {
    return (
      <Checklist
        checklistId={task.id}
        task={task}
        icon={task.id}
        {...config}
        {...clickHandlers}
      />
    );
  },
  jobSearchSummary: ({ task, clickHandlers }) => {
    return <JobSearchSummary task={task} {...clickHandlers} />;
  },
  preAssessmentSummary: ({ task, clickHandlers }) => {
    return <PreAssessmentSummary task={task} {...clickHandlers} />;
  },
  location: ({ config: locationId, task }) => {
    return <LocationFinder locationId={locationId} task={task} />;
  },
  registration: ({ config, clickHandlers, task }) => {
    return <Registration taskId={task.id} {...config} {...clickHandlers} />;
  },
  questionnaire: ({
    config: { questionnaireId },
    task,
    clickHandlers,
    firstModule,
  }) => {
    return (
      <Questionnaire
        qnaireId={questionnaireId}
        taskId={task.id}
        {...clickHandlers}
        firstModule={firstModule}
      />
    );
  },
  questionnaireDisclaimer: ({ config: { questionnaireId }, clickHandlers }) => {
    return (
      <QuestionnaireResultDisclaimer
        qnaireId={questionnaireId}
        {...clickHandlers}
      />
    );
  },
  page: ({ config: { pageId }, clickHandlers }) => {
    return <Page pageId={pageId} {...clickHandlers} />;
  },
  productChooser: ({ config: { productGroupId }, task, clickHandlers }) => {
    return (
      <ProductChooser
        productGroupId={productGroupId}
        taskId={task.id}
        {...clickHandlers}
      />
    );
  },
  productPage: ({
    config: { doneOnClose, productId },
    task,
    clickHandlers,
  }) => {
    return (
      <ProductPage
        doneOnClose={doneOnClose}
        productId={productId}
        taskId={task.id}
        {...clickHandlers}
      />
    );
  },
  salaryCalculator: ({ task, clickHandlers }) => {
    return <SalaryCalculator task={task} {...clickHandlers} />;
  },
};

async function onDoneClick(dispatch, task, closeMethod, tasksLength) {
  if (task.id === "legal_requirement_check" && tasksLength === 1) {
    await dispatch(setHideFullTaskList(true));
  }
  // NOTE: At some point that should be done via socket, but it is good enough for now
  await dispatch(fetchTasks());
  closeMethod();
}

async function handleBackClick(dispatch, task, currentModuleNr) {
  // FIXME: Currently there is no way to skip one module in task depending on state
  const skipRegistration =
    (task.id === "pre_assessment" || task.id === "legal_requirement_check") &&
    currentModuleNr == 2;
  const skipCount = skipRegistration ? 2 : 1;
  try {
    if (currentModuleNr > 0) {
      await dispatch(
        changeModule({
          task,
          moduleIndex: (task.data.currentModule || 1) - skipCount,
        })
      );
    } else {
      await dispatch(restartTask(task));
    }
  } catch (err) {
    sendError(err);
  }
}

async function handleModuleFinished(dispatch, currentModuleNr, task) {
  const { modules } = task;
  const action =
    !currentModuleNr || modules.length > currentModuleNr
      ? changeModule({ task, moduleIndex: (currentModuleNr || 0) + 1 }) // Go to next module
      : markTaskAsDone(task); // This was the last module already
  try {
    await dispatch(action);
  } catch (err) {
    sendError(err);
  }
}

async function handleRestart(dispatch, task) {
  try {
    await dispatch(restartTask(task));
  } catch (err) {
    sendError(err);
  }
}

export default function TaskModule({ task, onClose }) {
  const tasksLength = useSelector(selectTaskLength);
  const dispatch = useDispatch();
  let currentModuleNr = task.data.currentModule || 0;
  let moduleData = task.modules[currentModuleNr];
  if (!moduleData) {
    sendError(`Task does not have module module number ${currentModuleNr}.`);
    currentModuleNr = 0;
    moduleData = task.modules[0];
  }
  const [moduleName, config] = moduleData;

  const clickHandlers = {
    onClose,
  };
  clickHandlers.onBack = () => handleBackClick(dispatch, task, currentModuleNr);
  if (currentModuleNr < task.modules.length - 1) {
    clickHandlers.onNext = () =>
      handleModuleFinished(dispatch, currentModuleNr, task);
  } else {
    clickHandlers.onNext = () =>
      onDoneClick(dispatch, task, onClose, tasksLength);
  }
  clickHandlers.onRestart = () => handleRestart(dispatch, task);

  const Module = MODULES[moduleName];
  if (!Module) {
    throw new Error(`Task module ${moduleName} is not defined.`);
  }

  const isProductChooser = moduleName === "productChooser";
  const isNarrow =
    moduleName === "questionnaire" || moduleName === "registration";

  if (task.blocked) {
    return <BlockedState task={task} {...clickHandlers} />;
  }

  return (
    <Fragment>
      <div class="row">
        <div
          class={classNames(
            "col s12",
            !isProductChooser && !isNarrow && "l8 offset-l2",
            isNarrow && "l6 offset-l2"
          )}
        >
          <Module
            {...{ config, task, clickHandlers }}
            firstModule={currentModuleNr === 0}
          />
        </div>
      </div>
      <TaskFooter
        innerClass={classNames(
          "col s12",
          !isProductChooser && !isNarrow && "l8 offset-l2",
          isNarrow && "l6 offset-l2"
        )}
      />
    </Fragment>
  );
}
